import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const API_URL = `${import.meta.env.VITE_API}analytics/visit`;

export default function AnalyticsTracker() {
  const location = useLocation();

  const pathRef = useRef(location.pathname);

  const lastActiveTimeRef = useRef(Date.now());

  const totalDurationRef = useRef(0);

  const isVisibleRef = useRef(
    typeof document !== "undefined" && document.visibilityState === "visible",
  );

  const hasFlushedRef = useRef(false);

  /**
   * Get current active duration in milliseconds.
   */
  const getCurrentDuration = useCallback(() => {
    let duration = totalDurationRef.current;

    if (isVisibleRef.current) {
      duration += Date.now() - lastActiveTimeRef.current;
    }

    return duration;
  }, []);

  /**
   * Send current visit to backend.
   */
  const flushVisit = useCallback(() => {
    if (hasFlushedRef.current) return;

    const duration = Math.floor(getCurrentDuration() / 1000);

    if (duration <= 0) {
      hasFlushedRef.current = true;
      return;
    }

    const payload = {
      path: pathRef.current,
      duration,
    };

    hasFlushedRef.current = true;

    fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      credentials: "omit",
      keepalive: true,
    }).catch(() => {
      // Analytics failure should never affect the application.
    });
  }, [getCurrentDuration]);

  /**
   * Handle route changes.
   */
  useEffect(() => {
    const newPath = location.pathname;

    if (newPath === pathRef.current) return;

    flushVisit();

    pathRef.current = newPath;

    totalDurationRef.current = 0;

    lastActiveTimeRef.current = Date.now();

    hasFlushedRef.current = false;
  }, [location.pathname, flushVisit]);

  /**
   * Handle tab visibility changes.
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (!isVisibleRef.current) return;

        totalDurationRef.current += Date.now() - lastActiveTimeRef.current;

        isVisibleRef.current = false;

        return;
      }

      if (document.visibilityState === "visible") {
        if (isVisibleRef.current) return;

        lastActiveTimeRef.current = Date.now();

        isVisibleRef.current = true;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  /**
   * Handle page refresh, browser close
   * and external navigation.
   */
  useEffect(() => {
    const handlePageHide = () => {
      flushVisit();
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [flushVisit]);

  /**
   * Initialize current visit.
   */
  useEffect(() => {
    pathRef.current = location.pathname;

    totalDurationRef.current = 0;

    lastActiveTimeRef.current = Date.now();

    hasFlushedRef.current = false;

    isVisibleRef.current = document.visibilityState === "visible";
  }, []);

  return null;
}
